import { createSelector, createStructuredSelector } from 'reselect';

export const MAX_POTENTIAL_RETURNS = 1000000;

export const getCurrentSlip = state => state.getIn(['bettingBuddy', 'currentSlip']);
export const getEventsDay = state => state.getIn(['bettingBuddy', 'eventsDay']);
export const getMarketGroupId = state => state.getIn(['bettingBuddy', 'marketGroupId']);
export const isLegsLimitExceeded = state => state.getIn(['bettingBuddy', 'legsLimitExceeded']);
export const isSlipSavePending = state => state.getIn(['bettingBuddy', 'slipSavePending']);
export const getSavedSlips = state => state.getIn(['bettingBuddy', 'savedSlips']);

export const getSavedSlipById = (state, { id }) => getSavedSlips(state).find(s => s.id === id);

export const isSelectionInSlip = (state, { selectionId }) => (
  !!getCurrentSlip(state).legs.find(l => l.selectionId === selectionId)
);

export const slipOdds = (slip) => slip.get('legs')
  .reduce((acc, next) => acc * ((next.selectionPriceNum / next.selectionPriceDen) + 1), 1) - 1;

export const slipOddsString = slip => `${slipOdds(slip).toFixed(2)}/1`;

export const slipPotentialReturns = (slip, stake) => {
  const slipStake = parseFloat(stake, 10);
  if (Number.isNaN(slipStake)) {
    return 0;
  }
  return Math.min((slipOdds(slip) * slipStake) + slipStake, MAX_POTENTIAL_RETURNS);
};

export const getCurrentSlipOdds = createSelector(
  getCurrentSlip,
  slipOddsString
);

export const getCurrentSlipSize = createSelector(
  getCurrentSlip,
  slip => slip.legs.size
);

export const hasCurrentSlip = createSelector(
  getCurrentSlip,
  slip => slip.legs.size !== 0
);

export const getCurrentSlipName = createSelector(getCurrentSlip, slip => slip.slipName);

export const getCurrentSlipStake = createSelector(
  getCurrentSlip,
  slip => slip.slipStake
);

export const convertPoundsToPence = pounds => {
  const floatPounds = parseFloat(pounds, 10);
  if (Number.isNaN(floatPounds)) {
    return 0;
  }
  return Math.round(floatPounds * 100);
};

export const getCurrentSlipStakeInPence = createSelector(
  getCurrentSlipStake,
  convertPoundsToPence
);

export const getCurrentSlipPotentialReturns = createSelector(
  getCurrentSlip,
  getCurrentSlipStake,
  slipPotentialReturns
);

export const getCurrentSlipRawSelections = createSelector(
  getCurrentSlip,
  slip => slip.legs
);

export const getCurrentSlipSelections = createSelector(
  getCurrentSlipRawSelections,
  selections => selections.map(selection => ({
    eventId: selection.eventId,
    eventName: selection.eventName,
    marketName: selection.marketName,
    selectionId: selection.selectionId,
    selectionName: selection.selectionName,
    price: `${selection.selectionPriceNum}/${selection.selectionPriceDen}`,
    startDateTime: selection.eventStartDateTime
  }))
);

export const getSlipToSave = createStructuredSelector({
  name: getCurrentSlipName,
  stake: getCurrentSlipStakeInPence,
  selections: getCurrentSlipSelections
});

export const getCurrentSlipStakeErrors = (
  state => state.getIn(['bettingBuddy', 'currentSlipStakeErrors'])
);

export const isSavedSlipsViewLoading = (
  state => state.getIn(['bettingBuddy', 'savedSlipsViewIsLoading'])
);

export const betType = slip => {
  switch (slip.legs.size) {
    case 1:
      return 'Single';
    case 2:
      return 'Double';
    case 3:
      return 'Treble';
    default:
      return `${slip.legs.size} fold accumulator`;
  }
};

export const getCurrentSlipBetType = createSelector(
  getCurrentSlip,
  betType
);

export const getSavedSlipsIds = createSelector(
  getSavedSlips,
  slips => slips.map(s => s.id)
);

export const withReturns = slip => slip
  .set('odds', slipOddsString(slip))
  .set('potentialReturns', slipPotentialReturns(slip, slip.slipStake));

export const getSavedSlipWithReturns = createSelector(
  getSavedSlipById,
  withReturns
);
